import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, cn } from '@beatq/ui';
import { PermissionGate, showToast } from '@beatq/shared';
import {
  merchantOpsPollMs,
  useMerchantOrderRealtime,
} from '../features/order-board/useMerchantOrderRealtime.js';
import {
  branchesFromEnvelope,
  listBranches,
} from '../features/branch-settings/branchSettingsApi.js';
import {
  listMerchantInboxWindow,
  listMerchantNotifications,
  inboxMetaFromEnvelope,
  markAllMerchantNotificationsRead,
  markMerchantNotificationRead,
} from '../features/notifications/merchantNotificationsApi.js';
import { filterMerchantInbox } from '../features/notifications/merchantInbox.js';
import { NotificationInbox } from '../features/notifications/NotificationInbox.js';
import { MerchantBellIcon } from './merchantIcons.js';
import { getMerchantRoleCodes } from './merchantNavAccess.js';

function useDesktopDrawer(): boolean {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = () => setDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return desktop;
}

export function MerchantNotificationsBell() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const desktop = useDesktopDrawer();
  const [open, setOpen] = useState(false);
  const [branchFilter, setBranchFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const bellRef = useRef<HTMLButtonElement>(null);
  const onFullPage = location.pathname.replace(/\/$/, '') === '/notification-drawer';

  const { live } = useMerchantOrderRealtime(true);
  const badgeQuery = useQuery({
    queryKey: ['merchant', 'notifications', 'inbox', 'badge'],
    queryFn: () => listMerchantNotifications(),
    refetchInterval: merchantOpsPollMs(live, 30_000),
    retry: 1,
  });
  const unread = inboxMetaFromEnvelope(badgeQuery.data).unreadCount ?? 0;

  const branchesQuery = useQuery({
    queryKey: ['merchant', 'branches'],
    queryFn: listBranches,
    enabled: open,
  });
  const branches = branchesFromEnvelope(branchesQuery.data);
  const scopedIds = branches.map((b) => b.id).filter((id): id is string => Boolean(id));

  const inboxQuery = useQuery({
    queryKey: ['merchant', 'notifications', 'inbox'],
    queryFn: () => listMerchantInboxWindow(),
    enabled: open,
    placeholderData: (previous) => previous,
  });
  const rawMessages = inboxQuery.data?.messages ?? [];
  const meta = inboxQuery.data?.meta ?? {};
  const messages = useMemo(
    () =>
      filterMerchantInbox(rawMessages, {
        branchId: branchFilter || null,
        scopedBranchIds: scopedIds,
        unreadOnly,
        category: categoryFilter || null,
        roles: getMerchantRoleCodes(),
      }),
    [rawMessages, branchFilter, scopedIds, unreadOnly, categoryFilter],
  );
  const categories = useMemo(
    () => [
      ...new Set(
        rawMessages.map((item) => item.category).filter((value): value is string => Boolean(value)),
      ),
    ],
    [rawMessages],
  );

  const markRead = useMutation({
    mutationFn: markMerchantNotificationRead,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant', 'notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: markAllMerchantNotificationsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant', 'notifications'] });
      showToast('success', 'All notifications marked read');
    },
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        bellRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function onBellClick() {
    if (onFullPage) return;
    if (desktop) {
      setOpen((value) => !value);
      return;
    }
    navigate('/notification-drawer');
  }

  return (
    <div className="m01-notif-root">
      <button
        type="button"
        ref={bellRef}
        className={cn('mer-notif-bell relative inline-flex items-center justify-center')}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        aria-controls={open ? 'merchant-notification-drawer' : undefined}
        title="Notifications"
        data-testid="merchant-notifications"
        data-unread={unread}
        onClick={onBellClick}
      >
        <MerchantBellIcon size={16} />
        {unread > 0 ? (
          <span className="mer-notif-badge" data-testid="merchant-notifications-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open && desktop ? (
        <aside
          id="merchant-notification-drawer"
          className="m01-inbox-drawer"
          role="complementary"
          aria-modal="false"
          aria-label="Notifications"
          data-testid="merchant-notification-drawer"
        >
          <div className="m01-inbox-drawer-head">
            <h2 className="m01-inbox-drawer-title">Notifications</h2>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setOpen(false);
                bellRef.current?.focus();
              }}
            >
              Close
            </Button>
          </div>
          <PermissionGate perm="PERM-notification.history.read">
            {inboxQuery.isLoading ? (
              <div className="m01-inbox-skeletons" aria-busy="true">
                <div className="m01-inbox-skeleton" />
                <div className="m01-inbox-skeleton" />
                <div className="m01-inbox-skeleton" />
              </div>
            ) : null}
            {inboxQuery.isError ? (
              <p className="text-danger">
                {(inboxQuery.error as Error).message}{' '}
                <Button type="button" variant="secondary" onClick={() => void inboxQuery.refetch()}>
                  Retry
                </Button>
              </p>
            ) : (
              <NotificationInbox
                messages={messages}
                meta={meta}
                branches={branches}
                branchFilter={branchFilter}
                onBranchFilter={setBranchFilter}
                unreadOnly={unreadOnly}
                onUnreadOnly={setUnreadOnly}
                categoryFilter={categoryFilter}
                onCategoryFilter={setCategoryFilter}
                categories={categories}
                onMarkRead={(id) => markRead.mutate(id)}
                onMarkAll={() => markAll.mutate()}
                markReadPending={markRead.isPending}
                markAllPending={markAll.isPending}
                cachedUnread={unread}
              />
            )}
          </PermissionGate>
          <p className="m01-inbox-full-link">
            <Link to="/notification-drawer" onClick={() => setOpen(false)}>
              Open full inbox
            </Link>
          </p>
        </aside>
      ) : null}
    </div>
  );
}
